import {
  useAppInsightsContext,
  useTrackMetric,
} from '@microsoft/applicationinsights-react-js';
import { type GetStaticProps } from 'next';
import Link from 'next/link';

import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../components/page/Layout';
import { SUPPORT_TYPEFORM_URL } from '../../utils/constants';

const TITLE = 'Refund Policy';
const META_DESCRIPTION = 'Leland Refund Policy';

const SPACING_CLASS = 'my-4';
const LINK_CLASS =
  'link underline text-leland-gray-light hover:text-leland-gray-extra-light';

const RefundPolicy: NextPageWithLayout = () => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'RefundPolicy');
  return (
    <div className="container-def px-4 py-8 lg:p-20">
      <h1 className="mb-8 text-5xl font-semibold">{TITLE}</h1>

      <p className={SPACING_CLASS}>
        Leland is a coaching marketplace that connects customers with
        independent coaches who can help them achieve their goals through 1:1
        coaching, classes, and content. Because there are different modes of
        delivering coaching, the refund policy varies by type of service
        purchased.
      </p>
      <p className={SPACING_CLASS}>
        For any inquiries regarding our refund policy,{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          please submit a support request here
        </a>
        .
      </p>

      <h2 className="subheading-text mt-6">Expiration Terms</h2>
      <p className={SPACING_CLASS}>
        Expiration terms dictate the timeframe within which clients must utilize
        the coaching services they have purchased. You can view the applicable
        refund policy and expiration terms during checkout. After purchasing,
        these details will also be accessible in your order history. Please note
        that refunds will not be processed for expired services.
      </p>

      <h2 className="subheading-text mt-6">Leland Experience Guarantee</h2>
      <p className={SPACING_CLASS}>
        We created the Leland Experience Guarantee to provide you with the
        confidence and peace of mind when booking a Leland coach. This guarantee
        comprises three pillars: top-quality coaches, first hour satisfaction,
        and ongoing support. You can{' '}
        <Link
          href="/legal/experience-guarantee"
          className={LINK_CLASS}
          id="refund_policy--experience_guarantee"
        >
          read more here
        </Link>
        .
      </p>

      <h2 className="subheading-text mt-6">
        One-on-One Coaching Refund Policy
      </h2>
      <p className={SPACING_CLASS}>
        Our standard refund policy for non-subscription one-on-one coaching
        services, including a la carte hours and packages, allows for a refund
        within 14 days from the date of purchase. Below is an outline of how
        refunds are processed:
      </p>
      <ol className={`${SPACING_CLASS} ml-4 list-decimal`}>
        <li>
          <span className="font-medium">Within 14 Days</span>: If a refund is
          requested within the 14-day period, clients may choose to receive a
          refund to their original payment method or opt for Leland coaching
          credit applicable to any service on the platform.
        </li>
        <li>
          <span className="font-medium">
            Beyond 14 Days, Within Expiration Terms
          </span>
          : If a refund request is made after the 14-day period but within the
          expiration terms, only Leland coaching credit will be issued.
        </li>
        <li>
          <span className="font-medium">
            Outside Both 14-Day and Expiration Periods
          </span>
          : Refund requests made beyond both the 14-day refund period and the
          expiration terms will not be eligible for any refund.
        </li>
      </ol>
      <p className={SPACING_CLASS}>
        For partially used services, refunds will be prorated based on the
        coach&rsquo;s hourly rate at time of purchase rather than the discounted
        package or bundle rates. Refunds will be processed either to your
        original payment method or as Leland coaching credit, in accordance with
        the terms outlined above. Important to note that split-payment fees are
        non-refundable.
      </p>

      <h2 className="subheading-text mt-6">
        Coaching Membership Subscription Refund Policy
      </h2>
      <p className={SPACING_CLASS}>
        Refunds for coaching membership subscriptions are available up until the
        start of your first coaching session or within 48 hours of the charge
        for subsequent months. For questions about your coaching subscription,{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          please submit a support request here
        </a>
        .
      </p>

      <h2 className="subheading-text mt-6">Leland+ Refund Policy</h2>
      <p className={SPACING_CLASS}>
        Refunds for Leland+ subscriptions are available before the initial
        access is granted in the first month or within 48 hours of the charge
        for subsequent months. For questions about your Leland+ subscription,
        please{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          please submit a support request here
        </a>
        .
      </p>

      <h2 className="subheading-text mt-6">Bootcamp Refund Policy</h2>
      <p className={SPACING_CLASS}>
        Full refunds for bootcamps are available up until the start of the first
        session. No refunds will be issued once the bootcamp has begun.
      </p>

      <h2 className="subheading-text mt-6">Classes Refund Policy</h2>
      <p className={SPACING_CLASS}>
        Unfortunately, due to the limited availability of class spots, we do not
        offer refunds for classes at this time.
      </p>

      <h2 className="subheading-text mt-6">Outcomes</h2>
      <p className={SPACING_CLASS}>
        Achieving specific career goals, test scores, or admission to top
        schools involves factors beyond our, your, and/or your coach&rsquo;s
        control. While working with a coach greatly increases your chances of
        success, we cannot guarantee your desired outcome. We do not provide
        refunds based on results or outcomes.
      </p>

      <h2 className="subheading-text mt-6">FAQs</h2>
      <p className={SPACING_CLASS}>
        <span className="font-medium">
          Q: Can I switch coaches and transfer my unused hours to a new coach?
        </span>
        <br />
        A: Coach switching for packages ≥ 10 hours is only allowed in the first
        two weeks of the coaching engagement. The two week period starts when
        you are officially matched with your coach. If you&rsquo;re not
        satisfied with your current coach, please{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          submit a support request here
        </a>{' '}
        to discuss your situation.
      </p>
      <p className={SPACING_CLASS}>
        <span className="font-medium">
          Q: What happens if my coach becomes unavailable during my coaching
          term?
        </span>
        <br />
        A: If your coach becomes unavailable during your coaching term, please{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          submit a support request here
        </a>
        . We will work with you to find an alternative coach.
      </p>
      <p className={SPACING_CLASS}>
        <span className="font-medium">
          Q: Are there any exceptions to the no-refund policy for classes?
        </span>
        <br />
        A: Exceptions to the no-refund policy for classes are made only in rare
        and extenuating circumstances, such as technical issues that prevent
        access to the service or a significant change in the class curriculum.
        In such cases, please{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          submit a support request here
        </a>{' '}
        with a detailed explanation of the issue so we can assess your case and
        provide an appropriate solution.
      </p>

      <h2 className="subheading-text mt-6">Still Have Questions?</h2>
      <p className={SPACING_CLASS}>
        Feel free to{' '}
        <a
          className={LINK_CLASS}
          href={SUPPORT_TYPEFORM_URL}
          id="refund_policy--submit_support_request"
        >
          submit a support request here
        </a>
        .
      </p>
    </div>
  );
};

RefundPolicy.getLayout = (page, { pageContentData }) => (
  <Layout
    title={TITLE}
    description={META_DESCRIPTION}
    canonicalLink="/legal/refund-policy"
    pageContentData={pageContentData}
  >
    {page}
  </Layout>
);

export const getStaticProps: GetStaticProps<{
  pageContentData: Possible<LayoutDataProps>;
}> = async () => {
  const layoutCategoriesHeaderData = await getStaticLayoutQuery();

  return { props: { pageContentData: layoutCategoriesHeaderData } };
};

export default RefundPolicy;
